import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert } from "@swot/base-components";
import { CmtsSmartDataGrid, type CmtsGridColumn } from "../data-grid/index.js";
import { evidenceApi } from "../../services/evidenceApi.js";
import { normalizeComplaintUiError } from "../../core/errors/cmtsComplaintErrorNormalizer.js";
import type { CmtsEvidenceRecord } from "../../core/complaints/cmtsComplaintLifecycleTypes.js";
import { recordString } from "../../core/complaints/cmtsComplaintLifecycleTypes.js";

const columns: readonly CmtsGridColumn<CmtsEvidenceRecord>[] = [
{ id: "fileName", header: "Evidence", minWidth: 240, sortable: true, render: (row) => recordString(row, ["originalFileName", "fileName", "name", "title"], "-") },  { id: "fileType", header: "Type", minWidth: 120, sortable: true, render: (row) => recordString(row, ["fileType", "mimeType"], "-") },
{ id: "scanStatus", header: "Scan", minWidth: 130, sortable: true, render: (row) => recordString(row, ["scanStateCode", "scanStatus", "status"], "-") },
  { id: "confidentiality", header: "Confidentiality", minWidth: 150, sortable: true, render: (row) => recordString(row, ["confidentialityCode", "confidentiality"], "-") },  
  { id: "uploadedAt", header: "Uploaded", minWidth: 160, sortable: true, render: (row) => recordString(row, ["uploadedAt", "createdAt"], "-") },
];

function downloadFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "cmts-evidence";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function CmtsEvidenceList({ complaintId, rows, onPreview }: { readonly complaintId: string; readonly rows: readonly CmtsEvidenceRecord[]; readonly onPreview: (row: CmtsEvidenceRecord) => void }) {  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const download = useMutation({ mutationFn: async (row: CmtsEvidenceRecord) => {
    const evidenceId = recordString(row, ["id", "evidenceId"], "");
    const tokenResponse = await evidenceApi.downloadToken(complaintId, evidenceId);
    const token = recordString(tokenResponse, ["token", "downloadToken"], "");
    if (!token) throw new Error("download_token_failed: backend did not return an evidence download token.");
    const blob = await evidenceApi.downloadBlob(token);
    downloadFile(blob, recordString(row, ["fileName", "name"], "cmts-evidence"));
  }, onError: setError });
  const deactivate = useMutation({ mutationFn: (row: CmtsEvidenceRecord) => evidenceApi.deactivate(complaintId, recordString(row, ["id", "evidenceId"], ""), "Deactivated from backend-synced evidence UI"), onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["cmts", "backend-evidence", complaintId] }), onError: setError });
  const remove = useMutation({ mutationFn: (row: CmtsEvidenceRecord) => evidenceApi.delete(complaintId, recordString(row, ["id", "evidenceId"], "")), onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["cmts", "backend-evidence", complaintId] }), onError: setError });
  const uiError = error ? normalizeComplaintUiError(error, "Evidence action failed.") : null;
  return (
    <>
      {uiError ? <Alert title="Evidence action failed" tone="danger">{uiError.userMessage}</Alert> : null}
      <CmtsSmartDataGrid<CmtsEvidenceRecord>
        title="Backend evidence register"
        rows={rows}
        columns={columns}
        rowKey="id"
        loading={download.isPending || deactivate.isPending || remove.isPending}
        emptyMessage="No evidence metadata returned by backend."
       actions={[
          { id: "preview", label: "Preview", onClick: (row) => onPreview(row) },
          { id: "download", label: "Download", onClick: (row) => download.mutateAsync(row) },
          { id: "deactivate", label: "Deactivate", onClick: (row) => deactivate.mutateAsync(row) },
          { id: "delete", label: "Delete", onClick: (row) => remove.mutateAsync(row) },
        ]}
      />
    </>
  );
}
